import React, { useState } from "react";
import {
  Plus,
  Gift,
  Tag,
  Info,
  Edit,
  Trash,
  AlertTriangle,
  Check,
  X,
  RefreshCw,
  Settings as SettingsIcon,
} from "lucide-react";
import {
  Button,
  IconButton,
  TextField,
  Switch,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardContent,
  Divider,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { successToast, errorToast } from "../../../services/toast.service";

const RedemptionRules = () => {
  // State for redemption rules
  const [redemptionRules, setRedemptionRules] = useState([
    {
      id: 1,
      name: "Discount Voucher - 10%",
      pointsRequired: 200,
      description: "Redeem 200 points for a 10% discount on your next purchase",
      isActive: true,
      redemptionLimit: 1,
      validityPeriod: 30, // days
      category: "Discount",
    },
    {
      id: 2,
      name: "Free Shipping",
      pointsRequired: 150,
      description: "Redeem 150 points for free shipping on your next order",
      isActive: true,
      redemptionLimit: 2,
      validityPeriod: 15, // days
      category: "Shipping",
    },
    {
      id: 3,
      name: "Premium Product",
      pointsRequired: 500,
      description: "Redeem 500 points for a free premium product",
      isActive: false,
      redemptionLimit: 1,
      validityPeriod: 60, // days
      category: "Product",
    },
    {
      id: 4,
      name: "Discount Voucher - 20%",
      pointsRequired: 400,
      description: "Redeem 400 points for a 20% discount on your next purchase",
      isActive: true,
      redemptionLimit: 1,
      validityPeriod: 30, // days
      category: "Discount",
    },
  ]);

  // State for rule form
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [currentRule, setCurrentRule] = useState({
    id: null,
    name: "",
    pointsRequired: 0,
    description: "",
    isActive: true,
    redemptionLimit: 1,
    validityPeriod: 30,
    category: "Discount",
  });

  // Filter state
  const [statusFilter, setStatusFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [sortBy, setSortBy] = useState("name");

  // State for delete confirmation
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [ruleToDelete, setRuleToDelete] = useState(null);

  // Toggle redemption rule active status
  const handleToggleStatus = id => {
    const updatedRules = redemptionRules.map(rule =>
      rule.id === id ? { ...rule, isActive: !rule.isActive } : rule,
    );
    setRedemptionRules(updatedRules);

    successToast("Rule status updated successfully");
  };

  // Open edit dialog
  const handleEditRule = rule => {
    setCurrentRule({ ...rule });
    setIsEditMode(true);
    setIsDialogOpen(true);
  };

  // Open delete confirmation dialog
  const handleDeleteClick = rule => {
    setRuleToDelete(rule);
    setDeleteDialogOpen(true);
  };

  // Delete rule
  const handleDeleteConfirm = () => {
    const updatedRules = redemptionRules.filter(
      rule => rule.id !== ruleToDelete.id,
    );
    setRedemptionRules(updatedRules);
    setDeleteDialogOpen(false);

    successToast("Rule deleted successfully");
  };

  // Open new rule dialog
  const handleAddRule = () => {
    setCurrentRule({
      id:
        redemptionRules.length > 0
          ? Math.max(...redemptionRules.map(r => r.id)) + 1
          : 1,
      name: "",
      pointsRequired: 0,
      description: "",
      isActive: true,
      redemptionLimit: 1,
      validityPeriod: 30,
      category: "Discount",
    });
    setIsEditMode(false);
    setIsDialogOpen(true);
  };

  // Save rule (create or update)
  const handleSaveRule = () => {
    if (isEditMode) {
      // Update existing rule
      const updatedRules = redemptionRules.map(rule =>
        rule.id === currentRule.id ? { ...currentRule } : rule,
      );
      setRedemptionRules(updatedRules);

      successToast("Rule updated successfully");
    } else {
      // Create new rule
      setRedemptionRules([...redemptionRules, currentRule]);

      successToast("New rule created successfully");
    }

    setIsDialogOpen(false);
  };

  // Handle form input changes
  const handleInputChange = e => {
    const { name, value } = e.target;
    const parsedValue = [
      "pointsRequired",
      "redemptionLimit",
      "validityPeriod",
    ].includes(name)
      ? parseInt(value)
      : value;

    setCurrentRule({
      ...currentRule,
      [name]: parsedValue,
    });
  };

  // Handle switch toggle
  const handleSwitchChange = e => {
    setCurrentRule({
      ...currentRule,
      isActive: e.target.checked,
    });
  };

  // Filter and sort rules
  const filteredRules = redemptionRules
    .filter(rule => {
      const matchesStatus =
        statusFilter === "all" ||
        (statusFilter === "active" && rule.isActive) ||
        (statusFilter === "inactive" && !rule.isActive);

      const matchesCategory =
        categoryFilter === "all" ||
        rule.category.toLowerCase() === categoryFilter.toLowerCase();

      return matchesStatus && matchesCategory;
    })
    .sort((a, b) => {
      if (sortBy === "name") {
        return a.name.localeCompare(b.name);
      } else if (sortBy === "points") {
        return b.pointsRequired - a.pointsRequired;
      } else if (sortBy === "category") {
        return a.category.localeCompare(b.category);
      }
      return 0;
    });

  // Get the available categories from the rules
  const categories = [
    "all",
    ...new Set(redemptionRules.map(rule => rule.category.toLowerCase())),
  ];

  // Filter rules into active and inactive
  const activeRules = filteredRules.filter(rule => rule.isActive);
  const inactiveRules = filteredRules.filter(rule => !rule.isActive);

  return (
    <div className="bg-white h-full flex flex-col">
      {/* Header Section - Updated to match image 1 */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E7EB]">
        <h1 className="text-2xl font-semibold font-outfit text-[#374151]">
          Redemption Rules
        </h1>
        <div className="flex items-center gap-2">
          <IconButton
            sx={{
              color: "#5652B0",
              border: "1px solid #E5E7EB",
              borderRadius: "4px",
              p: "8px",
            }}>
            <RefreshCw size={18} />
          </IconButton>
          <IconButton
            sx={{
              color: "#5652B0",
              border: "1px solid #E5E7EB",
              borderRadius: "4px",
              p: "8px",
            }}>
            <SettingsIcon size={18} />
          </IconButton>
          <Button
            variant="contained"
            startIcon={<Plus size={18} />}
            onClick={handleAddRule}
            sx={{
              backgroundColor: "#5652B0",
              "&:hover": { backgroundColor: "#4B47A1" },
              borderRadius: "8px",
              textTransform: "none",
              fontWeight: 600,
            }}>
            Add New Rule
          </Button>
        </div>
      </div>

      {/* Combined Filter Controls and Navigation Buttons */}
      <div className="flex items-center justify-between py-4 px-6 border-b border-[#E5E7EB]">
        {/* Status Filter Buttons on Left */}
        <div className="flex items-center gap-3">
          <button
            onClick={() => setStatusFilter("all")}
            className={`flex items-center gap-2 py-2 px-3 rounded-lg transition-all ${
              statusFilter === "all"
                ? "bg-purple-100 text-[#5652B0]"
                : "bg-white hover:bg-gray-50 border border-gray-200"
            }`}>
            <div className="w-7 h-7 rounded-full bg-purple-100 flex items-center justify-center">
              <Gift size={14} className="text-purple-800" />
            </div>
            <div className="flex flex-col items-start">
              <span className="text-xs font-medium">Total Rules</span>
              <span className="text-sm font-semibold">
                {redemptionRules.length}
              </span>
            </div>
          </button>

          <button
            onClick={() => setStatusFilter("active")}
            className={`flex items-center gap-2 py-2 px-3 rounded-lg transition-all ${
              statusFilter === "active"
                ? "bg-green-100 text-green-700"
                : "bg-white hover:bg-gray-50 border border-gray-200"
            }`}>
            <div className="w-7 h-7 rounded-full bg-green-100 flex items-center justify-center">
              <Check size={14} className="text-green-600" />
            </div>
            <div className="flex flex-col items-start">
              <span className="text-xs font-medium">Active Rules</span>
              <span className="text-sm font-semibold">
                {redemptionRules.filter(r => r.isActive).length}
              </span>
            </div>
          </button>

          <button
            onClick={() => setStatusFilter("inactive")}
            className={`flex items-center gap-2 py-2 px-3 rounded-lg transition-all ${
              statusFilter === "inactive"
                ? "bg-gray-100 text-gray-700"
                : "bg-white hover:bg-gray-50 border border-gray-200"
            }`}>
            <div className="w-7 h-7 rounded-full bg-gray-100 flex items-center justify-center">
              <X size={14} className="text-gray-500" />
            </div>
            <div className="flex flex-col items-start">
              <span className="text-xs font-medium">Inactive Rules</span>
              <span className="text-sm font-semibold">
                {redemptionRules.filter(r => !r.isActive).length}
              </span>
            </div>
          </button>
        </div>

        {/* Category and Sort Dropdowns on Right */}
        <div className="flex items-center gap-4">
          <div>
            <select
              value={categoryFilter}
              onChange={e => setCategoryFilter(e.target.value)}
              className="w-[240px] h-[44px] px-4 py-2 border border-gray-200 rounded-lg bg-white text-gray-800 focus:border-[#5652B0] focus:ring-1 focus:ring-[#5652B0] outline-none appearance-none cursor-pointer"
              style={{
                backgroundImage: `url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e")`,
                backgroundRepeat: "no-repeat",
                backgroundPosition: "right 10px center",
                backgroundSize: "16px",
              }}>
              <option value="all">All Categories</option>
              {categories
                .filter(c => c !== "all")
                .map(category => (
                  <option key={category} value={category}>
                    {category.charAt(0).toUpperCase() + category.slice(1)}
                  </option>
                ))}
            </select>
          </div>

          <div>
            <select
              value={sortBy}
              onChange={e => setSortBy(e.target.value)}
              className="w-[240px] h-[44px] px-4 py-2 border border-gray-200 rounded-lg bg-white text-gray-800 focus:border-[#5652B0] focus:ring-1 focus:ring-[#5652B0] outline-none appearance-none cursor-pointer"
              style={{
                backgroundImage: `url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e")`,
                backgroundRepeat: "no-repeat",
                backgroundPosition: "right 10px center",
                backgroundSize: "16px",
              }}>
              <option value="name">Name</option>
              <option value="points">Points Required</option>
              <option value="category">Category</option>
            </select>
          </div>
        </div>
      </div>

      {/* Rules List - Modified to be more compact */}
      <div className="flex-1 overflow-auto p-4">
        <div>
          {filteredRules.length === 0 ? (
            <div className="bg-white rounded-lg border border-[#E5E7EB] p-6 text-center">
              <p className="text-gray-500">
                No {statusFilter} redemption rules found.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {filteredRules.map(rule => (
                <Card
                  key={rule.id}
                  variant="outlined"
                  sx={{
                    borderRadius: "8px",
                    opacity: rule.isActive ? 1 : 0.7,
                    backgroundColor: rule.isActive ? "#fff" : "#FAFAFA",
                  }}>
                  <CardContent className="p-0">
                    <div className="p-3">
                      <div className="flex justify-between items-center mb-1">
                        <h3
                          className="text-base font-medium text-gray-800 truncate mr-2"
                          title={rule.name}>
                          {rule.name}
                        </h3>
                        <Switch
                          checked={rule.isActive}
                          onChange={() => handleToggleStatus(rule.id)}
                          color="primary"
                          size="small"
                        />
                      </div>

                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        <span className="text-xs text-gray-500 border border-gray-200 rounded px-2 py-0.5">
                          {rule.category}
                        </span>
                        <div className="text-xs font-medium bg-purple-100 text-purple-800 px-2 py-0.5 rounded">
                          {rule.pointsRequired} points
                        </div>
                      </div>

                      <p
                        className="text-xs text-gray-600 mb-2 line-clamp-2"
                        title={rule.description}>
                        {rule.description}
                      </p>

                      <div className="flex flex-wrap gap-1 text-xs text-gray-500">
                        <span className="bg-gray-100 px-2 py-0.5 rounded">
                          Limit: {rule.redemptionLimit}
                        </span>
                        <span className="bg-gray-100 px-2 py-0.5 rounded">
                          Valid: {rule.validityPeriod}d
                        </span>
                      </div>
                    </div>

                    <div className="border-t border-gray-100 p-1 flex justify-end gap-1">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleEditRule(rule)}
                        aria-label="edit rule">
                        <Edit size={16} />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDeleteClick(rule)}
                        aria-label="delete rule">
                        <Trash size={16} />
                      </IconButton>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Rule Form Dialog */}
      <Dialog
        open={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        maxWidth="sm"
        fullWidth>
        <DialogTitle>
          {isEditMode ? "Edit Redemption Rule" : "Create New Redemption Rule"}
        </DialogTitle>
        <DialogContent dividers>
          <div className="space-y-4 py-2">
            <TextField
              label="Rule Name"
              name="name"
              value={currentRule.name}
              onChange={handleInputChange}
              fullWidth
              required
              variant="outlined"
              placeholder="e.g., Discount Voucher - 10%"
            />

            <TextField
              label="Category"
              name="category"
              value={currentRule.category}
              onChange={handleInputChange}
              fullWidth
              variant="outlined"
              placeholder="e.g., Discount, Product, Shipping"
            />

            <TextField
              label="Points Required"
              name="pointsRequired"
              type="number"
              value={currentRule.pointsRequired}
              onChange={handleInputChange}
              fullWidth
              required
              variant="outlined"
              InputProps={{ inputProps: { min: 1 } }}
            />

            <TextField
              label="Description"
              name="description"
              value={currentRule.description}
              onChange={handleInputChange}
              fullWidth
              multiline
              rows={3}
              variant="outlined"
              placeholder="Describe what customers get when they redeem these points"
            />

            <div className="grid grid-cols-2 gap-4">
              <TextField
                label="Redemption Limit"
                name="redemptionLimit"
                type="number"
                value={currentRule.redemptionLimit}
                onChange={handleInputChange}
                fullWidth
                variant="outlined"
                InputProps={{ inputProps: { min: 1 } }}
                helperText="Max redemptions per customer"
              />

              <TextField
                label="Validity Period (days)"
                name="validityPeriod"
                type="number"
                value={currentRule.validityPeriod}
                onChange={handleInputChange}
                fullWidth
                variant="outlined"
                InputProps={{ inputProps: { min: 1 } }}
                helperText="How long the reward is valid for"
              />
            </div>

            <FormControlLabel
              control={
                <Switch
                  checked={currentRule.isActive}
                  onChange={handleSwitchChange}
                  color="primary"
                />
              }
              label="Active"
            />
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsDialogOpen(false)} variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={handleSaveRule}
            variant="contained"
            disabled={!currentRule.name || currentRule.pointsRequired <= 0}
            sx={{
              backgroundColor: "#5652B0",
              "&:hover": { backgroundColor: "#4B47A1" },
            }}>
            {isEditMode ? "Update Rule" : "Create Rule"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="xs"
        fullWidth>
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <div className="flex items-start py-2">
            <AlertTriangle size={24} className="text-amber-500 mr-3 mt-0.5" />
            <div>
              <p className="text-gray-800">
                Are you sure you want to delete the rule{" "}
                <strong>{ruleToDelete?.name}</strong>?
              </p>
              <p className="text-gray-500 text-sm mt-1">
                This action cannot be undone. Any active redemptions will remain
                valid until they expire.
              </p>
            </div>
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            color="error">
            Delete Rule
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default RedemptionRules;
